import React, { useEffect, useRef, useState } from 'react';
import { Vendor } from '../types';
import { useVendors } from '../hooks/useVendors';
import useSEO from '../hooks/useSEO';
import { Button, Field, FlowPage, Icon } from '../components/bx';
import { flowSteps } from './ItemsPage';
import '../styles/bx.css';

interface VendorPageProps {
  isGetMe?: boolean;
  isSubscribe?: boolean;
  selectedVendor: Vendor | null;
  onVendorChange: (vendor: Vendor) => void;
  onContinue: () => void;
  onBack: () => void;
}

export const subscriptionSteps = ['Plan', 'Customise', 'Vendor', 'Checkout'];
const idOf = (v: Vendor | null | undefined) => v?._id || v?.id;

export default function VendorPage({ isGetMe, isSubscribe, selectedVendor, onVendorChange, onContinue, onBack }: VendorPageProps) {
  const { vendors, loading, error: apiError, hasMore, loadMore } = useVendors({ isSubscribe });
  const [error, setError] = useState('');
  const [customOpen, setCustomOpen] = useState(Boolean(selectedVendor?.isCustom));
  const [customName, setCustomName] = useState(selectedVendor?.isCustom ? selectedVendor.name : '');
  const sentinel = useRef<HTMLDivElement>(null);

  useSEO({
    title: 'Choose a vendor - BOQBOX',
    description: 'Pick the store BOQBOX should fulfil your order from.',
  });

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !('IntersectionObserver' in window) || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && !loading && loadMore(), { rootMargin: '300px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore, vendors.length]);

  const choose = (vendor: Vendor) => {
    onVendorChange(vendor);
    setCustomOpen(false);
    setError('');
  };

  const applyCustomVendor = () => {
    if (!customName.trim()) {
      setError('Enter the vendor’s name.');
      return false;
    }
    onVendorChange({ _id: 'custom', id: 'custom', name: customName.trim(), emoji: '🏬', rating: 0, deliveryTime: 'N/A', type: 'custom', isCustom: true });
    setError('');
    return true;
  };

  const handleContinue = () => {
    if (customOpen && !applyCustomVendor()) return;
    if (!customOpen && !selectedVendor) {
      setError('Choose a vendor to continue.');
      return;
    }
    onContinue();
  };

  const selectedName = customOpen ? customName || 'Your vendor' : selectedVendor?.name;

  return (
    <FlowPage
      eyebrow={isSubscribe ? 'Subscription' : isGetMe ? 'Get me' : 'Send a gift'}
      title="Choose a vendor"
      subtitle={isSubscribe ? 'Pick where your subscription should be fulfilled from.' : 'Pick the store we should shop from. You can name your own if it isn’t listed.'}
      steps={isSubscribe ? subscriptionSteps : flowSteps(isGetMe)}
      step={isSubscribe ? 2 : 1}
      onBack={onBack}
      narrow
      bar={
        <>
          <div className="bx-bar-copy">
            <small>{error ? <span style={{ color: 'var(--bx-danger)' }}>{error}</span> : 'Vendor'}</small>
            <strong>{selectedName || 'None selected'}</strong>
          </div>
          <Button onClick={handleContinue}>Continue <Icon name="arrow" /></Button>
        </>
      }
    >
      {apiError ? <div className="bx-alert" style={{ marginBottom: 14 }}>We couldn’t load vendors: {apiError}</div> : null}

      <div className="bx-options" role="radiogroup" aria-label="Vendors">
        {vendors.map((vendor) => {
          const on = !customOpen && idOf(selectedVendor) === idOf(vendor);
          return (
            <button key={idOf(vendor)} type="button" role="radio" aria-checked={on} className={`bx-option${on ? ' on' : ''}`} onClick={() => choose(vendor)}>
              <span className="bx-option-icon" aria-hidden="true">{vendor.emoji && vendor.emoji !== '?' ? vendor.emoji : '🏪'}</span>
              <span className="bx-option-copy">
                <strong>{vendor.name}</strong>
                {vendor.address ? <span>{vendor.address}</span> : null}
                <span className="bx-option-meta">
                  {vendor.rating ? <span className="bx-pill"><Icon name="star" size={13} /> {Number(vendor.rating).toFixed(1)}</span> : null}
                  {vendor.deliveryTime && vendor.deliveryTime !== 'N/A' ? <span className="bx-pill"><Icon name="clock" size={13} /> {vendor.deliveryTime}</span> : null}
                </span>
              </span>
              <span className="bx-radio" aria-hidden="true" />
            </button>
          );
        })}

        {loading ? Array.from({ length: vendors.length ? 1 : 3 }, (_, i) => <div key={i} className="bx-skel" style={{ height: 88, borderRadius: 16 }} />) : null}
        <div ref={sentinel} className="bx-sentinel" />

        {!isSubscribe ? (
          <div className={`bx-option${customOpen ? ' on' : ''}`} style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}>
            <button type="button" role="radio" aria-checked={customOpen} onClick={() => { setCustomOpen(true); setError(''); }} style={{ display: 'flex', gap: 14, alignItems: 'center', background: 'none', border: 0, padding: 0, color: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%' }}>
              <span className="bx-option-icon" aria-hidden="true">✍️</span>
              <span className="bx-option-copy"><strong>My vendor isn’t listed</strong><span>Tell us the store and we’ll shop there.</span></span>
              <span className="bx-radio" aria-hidden="true" />
            </button>
            {customOpen ? (
              <div style={{ marginTop: 14 }}>
                <Field label="Vendor name">
                  <input className="bx-input" autoFocus value={customName} placeholder="e.g. Shoprite Lekki" onChange={(e) => { setCustomName(e.target.value); setError(''); }} onKeyDown={(e) => e.key === 'Enter' && handleContinue()} />
                </Field>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </FlowPage>
  );
}
